import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  layoutTypes,
  leftSidebarTypes,
  layoutModeTypes,
  layoutWidthTypes,
  layoutPositionTypes,
  topbarThemeTypes,
  leftsidbarSizeTypes,
  leftSidebarViewTypes,
  leftSidebarImageTypes,
} from "../Components/constants/layout";

const LayoutContext = createContext(null);

const INIT_STATE = {
  layoutType: layoutTypes.VERTICAL,
  leftSidebarType: leftSidebarTypes.DARK,
  layoutModeType: layoutModeTypes.LIGHTMODE,
  layoutWidthType: layoutWidthTypes.FLUID,
  layoutPositionType: layoutPositionTypes.FIXED,
  topbarThemeType: topbarThemeTypes.LIGHT,
  leftsidbarSizeType: leftsidbarSizeTypes.DEFAULT,
  leftSidebarViewType: leftSidebarViewTypes.DEFAULT,
  leftSidebarImageType: leftSidebarImageTypes.NONE,
};

function readStoredLayoutMode() {
  try {
    const stored = localStorage.getItem("layoutMode");
    if (
      stored === layoutModeTypes.LIGHTMODE ||
      stored === layoutModeTypes.DARKMODE
    ) {
      return stored;
    }
  } catch (_) {
    /* ignore */
  }
  return INIT_STATE.layoutModeType;
}

export const LayoutProvider = ({ children }) => {
  const [layoutType, setLayoutType] = useState(INIT_STATE.layoutType);
  const [leftSidebarType, setLeftSidebarType] = useState(
    INIT_STATE.leftSidebarType
  );
  const [layoutModeType, setLayoutModeType] = useState(readStoredLayoutMode);
  const [layoutWidthType, setLayoutWidthType] = useState(
    INIT_STATE.layoutWidthType
  );
  const [layoutPositionType, setLayoutPositionType] = useState(
    INIT_STATE.layoutPositionType
  );
  const [topbarThemeType, setTopbarThemeType] = useState(
    INIT_STATE.topbarThemeType
  );
  const [leftsidbarSizeType, setLeftsidebarSizeType] = useState(
    INIT_STATE.leftsidbarSizeType
  );
  const [leftSidebarViewType, setLeftSidebarViewType] = useState(
    INIT_STATE.leftSidebarViewType
  );
  const [leftSidebarImageType, setLeftSidebarImageType] = useState(
    INIT_STATE.leftSidebarImageType
  );

  const changeLayout = useCallback((value) => setLayoutType(value), []);
  const changeSidebarTheme = useCallback(
    (value) => setLeftSidebarType(value),
    []
  );
  const changeLayoutMode = useCallback((value) => setLayoutModeType(value), []);
  const changeLayoutWidth = useCallback(
    (value) => setLayoutWidthType(value),
    []
  );
  const changeLayoutPosition = useCallback(
    (value) => setLayoutPositionType(value),
    []
  );
  const changeTopbarTheme = useCallback(
    (value) => setTopbarThemeType(value),
    []
  );
  const changeLeftsidebarSizeType = useCallback(
    (value) => setLeftsidebarSizeType(value),
    []
  );
  const changeLeftsidebarViewType = useCallback(
    (value) => setLeftSidebarViewType(value),
    []
  );
  const changeSidebarImageType = useCallback(
    (value) => setLeftSidebarImageType(value),
    []
  );

  // Thème clair/sombre : les variables SCSS (_theme-dark.scss) reposent sur
  // `body[data-layout-mode="light"|"dark"]` — sans cela le toggle ne change que le state React.
  useEffect(() => {
    document.body.setAttribute("data-layout-mode", layoutModeType);
  }, [layoutModeType]);

  const value = useMemo(
    () => ({
      layoutType,
      leftSidebarType,
      layoutModeType,
      layoutWidthType,
      layoutPositionType,
      topbarThemeType,
      leftsidbarSizeType,
      leftSidebarViewType,
      leftSidebarImageType,
      changeLayout,
      changeSidebarTheme,
      changeLayoutMode,
      changeLayoutWidth,
      changeLayoutPosition,
      changeTopbarTheme,
      changeLeftsidebarSizeType,
      changeLeftsidebarViewType,
      changeSidebarImageType,
    }),
    [
      layoutType,
      leftSidebarType,
      layoutModeType,
      layoutWidthType,
      layoutPositionType,
      topbarThemeType,
      leftsidbarSizeType,
      leftSidebarViewType,
      leftSidebarImageType,
      changeLayout,
      changeSidebarTheme,
      changeLayoutMode,
      changeLayoutWidth,
      changeLayoutPosition,
      changeTopbarTheme,
      changeLeftsidebarSizeType,
      changeLeftsidebarViewType,
      changeSidebarImageType,
    ]
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayout must be used within a LayoutProvider");
  return ctx;
};
